import React from 'react'
import { Chip, Tooltip } from '@mui/material'
import {
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material'
import { useAppSelector } from '@store/hooks'

const OfflineIndicator: React.FC = () => {
  const { isOnline, pendingCount, syncStatus } = useAppSelector((state) => state.offline)

  if (isOnline && pendingCount === 0 && syncStatus === 'idle') {
    return null
  }

  if (!isOnline) {
    return (
      <Tooltip title={pendingCount > 0 ? `${pendingCount} change(s) will sync when back online` : 'You are offline'}>
        <Chip
          icon={<WifiOffIcon />}
          label={pendingCount > 0 ? `Offline · ${pendingCount} pending` : 'Offline'}
          color="warning"
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      </Tooltip>
    )
  }

  if (syncStatus === 'syncing') {
    return (
      <Tooltip title="Syncing offline changes...">
        <Chip
          icon={<SyncIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />}
          label={`Syncing ${pendingCount} change(s)...`}
          color="info"
          size="small"
        />
      </Tooltip>
    )
  }

  if (syncStatus === 'success') {
    return (
      <Tooltip title="All changes synced successfully">
        <Chip
          icon={<CheckCircleIcon />}
          label="Synced"
          color="success"
          size="small"
        />
      </Tooltip>
    )
  }

  if (syncStatus === 'error') {
    return (
      <Tooltip title="Sync failed. Will retry automatically.">
        <Chip
          icon={<ErrorIcon />}
          label={`Sync error · ${pendingCount} pending`}
          color="error"
          size="small"
        />
      </Tooltip>
    )
  }

  if (pendingCount > 0) {
    return (
      <Tooltip title={`${pendingCount} change(s) pending sync`}>
        <Chip
          icon={<WifiIcon />}
          label={`${pendingCount} pending`}
          color="default"
          size="small"
        />
      </Tooltip>
    )
  }

  return null
}

export default OfflineIndicator
